import * as React from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Cross1Icon } from '@radix-ui/react-icons'
import { useUserData } from '~/hooks-data/use-user-data'
import { setLastAnnouncement } from './server'
import { env } from '~/env.js'
import { startLauncherPortalId } from '~/components/start-launcher/constants'
import { basehub, fragmentOn } from 'basehub'
import { RichText } from 'basehub/react-rich-text'

const AnnouncementsDates = {
  'edit-mode': '2024-05-05T04:00:00.000Z',
  [startLauncherPortalId]: '2025-07-15T04:00:00.000Z',
  agents: '2025-07-17T04:00:00.000Z',
} as const

type BaseHubAnnouncementId = keyof typeof AnnouncementsDates

const announcement = fragmentOn('AsFloatingBannerComponent', {
  __typename: true,
  _id: true,
  title: {
    json: {
      content: true,
    },
  },
  body: {
    json: { content: true },
  },
})

type Announcement = fragmentOn.infer<typeof announcement>

type RootProps = {
  children:
    | React.ReactNode
    | ((props: { isHighlighted: boolean }) => React.ReactNode)
  announcementId: BaseHubAnnouncementId
  glow?: boolean
} & Pick<Popover.PopoverContentProps, 'side' | 'align'>

const Root: React.FunctionComponent<RootProps> = ({
  children,
  announcementId,
  glow = true,
  ...rest
}) => {
  const [open, setOpen] = React.useState<boolean>()
  const user = useUserData(null).data
  const currentAnnouncementDate = React.useMemo(
    () => AnnouncementsDates[announcementId],
    [announcementId]
  )

  const [content, setContent] = React.useState<Announcement | null>(null)

  React.useEffect(() => {
    async function fetchData() {
      const result = await basehub({
        draft: false,
        token: env.NEXT_PUBLIC_BASEHUB_TOKEN,
      }).query({
        _componentInstances: {
          asFloatingBanner: {
            __args: {
              filter: {
                attachTo: {
                  eq: announcementId,
                },
              },
            },
            item: announcement,
          },
        },
      })

      setContent(result._componentInstances.asFloatingBanner.item)
    }

    fetchData()
  }, [announcementId])

  React.useEffect(() => {
    if (env.NEXT_PUBLIC_DISABLE_ANNOUNCEMENTS) return
    const timeout = setTimeout(() => {
      if (!user?.id) return
      const lastAnnouncementReceived = (sessionStorage.getItem(
        'announcement-received'
      ) ?? user.lastAnnouncementReceived) as BaseHubAnnouncementId | null

      // The user already saw this announcement or a newer one
      const lastDate = lastAnnouncementReceived
        ? AnnouncementsDates[lastAnnouncementReceived]
        : null
      if (
        lastAnnouncementReceived === announcementId ||
        (lastDate && lastDate > currentAnnouncementDate)
      ) {
        return setOpen(undefined)
      }

      // There's a newer announcement, don't show it
      if (
        Object.entries(AnnouncementsDates).some(
          ([key, date]) =>
            date > currentAnnouncementDate && key !== announcementId
        )
      ) {
        return setOpen(undefined)
      }

      // If start date was more than 2 weeks ago, don't show it
      if (
        new Date(currentAnnouncementDate) <
        new Date(Date.now() - 1000 * 60 * 60 * 24 * 14)
      ) {
        return setOpen(undefined)
      }

      // Don't show if user joined after the announcement
      if (user.joined && user.joined > currentAnnouncementDate) {
        return setOpen(undefined)
      }

      setOpen(true)
    }, 1000)

    return () => clearTimeout(timeout)
  }, [
    currentAnnouncementDate,
    announcementId,
    user?.id,
    user?.joined,
    user?.lastAnnouncementReceived,
  ])

  const handleChange = React.useCallback(
    (open: boolean) => {
      if (open) {
        setOpen(true)
        return
      }

      setOpen(false)
      if (user?.id) {
        setLastAnnouncement({
          announcementId: announcementId,
          userId: user.id,
        })
      }
      sessionStorage.setItem('announcement-received', announcementId)
    },
    [announcementId, user?.id]
  )

  if (open === undefined || !content) {
    return children instanceof Function
      ? children({ isHighlighted: false })
      : children
  }

  return (
    <>
      {open && (
        <div className="bg-dark-bg bg-opacity-80 top-0 left-0 fixed w-screen h-screen z-overlay animate-overlayShow duration-500" />
      )}
      <Popover.Root open={open} onOpenChange={handleChange}>
        <Popover.Anchor className="z-modal relative">
          {glow && open && (
            <div className="bg-dark-accent pointer-events-none scale-0 bg-opacity-50 size-full absolute top-0 left-0 blur-md rounded-full animate-scale-in duration-300 delay-100" />
          )}
          {children instanceof Function
            ? children({ isHighlighted: open })
            : children}
          {/* <div
            ref={mergeRefs([maskRef, elementRef])}
            className={clsx(
              maskClassName,
              'border border-dark-accent size-full left-0 -top-[0.5px] absolute'
            )}
          /> */}
        </Popover.Anchor>
        <Banner {...rest} dynamicContent={content} />
      </Popover.Root>
    </>
  )
}

const Banner = ({
  dynamicContent,
  ...rest
}: Popover.PopoverContentProps & {
  dynamicContent: Announcement
}) => {
  return (
    <Popover.Portal>
      <Popover.Content
        {...rest}
        sideOffset={9}
        collisionPadding={16}
        className="opacity-0 invisible items-center z-modal border border-dark-border-solid rounded-md gap-y-0.5 bg-dark-shade text-dark-muted grid grid-cols-[1fr_auto] pl-3 p-2 animate-ease-out delay-100"
      >
        <h3 className="text-dark-bright text-sm font-medium">
          <RichText
            content={dynamicContent.title.json.content}
            components={{
              p: (props) => <span {...props} />,
            }}
          />
        </h3>
        <Popover.Close className="hover:text-dark-dim transition-colors cursor-default text-dark-faint size-fit p-[3px]">
          <Cross1Icon height={12} width={12} />
        </Popover.Close>
        <RichText
          content={dynamicContent.body.json.content}
          components={{
            p: ({ children, ...props }) => (
              <p
                className="text-xs max-w-[217px] col-span-full mr-1"
                {...props}
              >
                {children}
              </p>
            ),
          }}
        />
      </Popover.Content>
    </Popover.Portal>
  )
}

export const AnnouncementHighlight = (props: RootProps) => {
  //guardrail
  if (!props.announcementId) {
    return props.children instanceof Function
      ? props.children({ isHighlighted: false })
      : props.children
  }

  return <Root {...props} />
}
